import React, { useCallback, use, useEffect } from "react";
import { useI18n } from "../hooks/i18n-hooks";
import VStack from "../ui_atoms/VStack";
import InvitationImage from "../assets/images/invitation_image.svg";
import ScreenCard from "./ScreenCard";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../navigations/RootNavigation";
import MarkdownView from "../components/MarkdownView";
import { escapeMarkdown } from "../utils";
import { ProfileSwitcherManagerContext } from "../contexts/ProfileSwitcherManager";
import { AuthManagerContext } from "../contexts/AuthManager";
import HStack from "../ui_atoms/HStack";
import UserAvatar from "./user/UserAvatar";
import { StyleSheet, View } from "react-native";
import apolloClient from "../configs/graphql.config";
import Gap from "../ui_atoms/Gap";
import { useTheme } from "../hooks/use-theme";
import Button from "../components/Button";
import { useMutation, useQuery } from "@apollo/client/react";
import { CREATE_WORKER } from "../graphql/core";
import { InvitationAction, useInvitationHandler } from "../hooks/invitations";
import { useSnackbar } from "../hooks/snackbar";
import { useSetAlert } from "../hooks/use-set-alert";
import { GET_INVITATION, INVITATION_BY_CODE, REJECT_INVITATION } from "../graphql/invitation";
import { InvitationTarget } from "../__generated__/graphql";
import { useSetAtom } from "jotai";
import { signupInvitationIdsToIgnore } from "../atoms/auth";
import { useOnBeforeScreenRemove } from "../hooks/use-before-screen-remove";
import { useCloseScreenThenNavigate } from "../hooks/use-close-screen-then-navigate";
import { GET_CLIENT_WORKERS_AND_INVITATION_WORKERS } from "../graphql/client";
import { useOpenMarkdownLink } from "../hooks/use-open-markdown-link";
import { useInvitationCodeErrorMessage } from "../hooks/invitation-code";

type InvitationProps = StackScreenProps<RootStackParamList, "Invitation">;

const Invitation = ({ navigation, route }: InvitationProps) => {
    const i18n = useI18n();
    const { profileType } = use(ProfileSwitcherManagerContext);
    // By code: an invitation sent by email, opened while signing up (there is no user yet to whom
    // it could belong). It is then accepted by the signup itself, which takes the code.
    const params: { invitationId?: string; invitationCode?: string } = route.params ?? {};
    const { invitationId, invitationCode } = params;
    const { user, refreshUser } = use(AuthManagerContext);
    const { showSnackbar } = useSnackbar();
    const { setAlert } = useSetAlert();
    const {
        data,
        error: getInvitationError,
        loading: getInvitationLoading,
    } = useQuery(GET_INVITATION, {
        variables: {
            invitationId: invitationId ?? "",
        },
        skip: !invitationId,
    });
    const { data: invitationByCodeData, error: invitationByCodeError } = useQuery(
        INVITATION_BY_CODE,
        {
            variables: { code: invitationCode ?? "" },
            skip: !invitationCode,
        },
    );
    const invitationByCode = invitationByCodeData?.invitationByCode;
    const errorMessageForCode = useInvitationCodeErrorMessage();

    const onActionSuccess = useCallback(
        (action: InvitationAction) => {
            if (["accept", "reject", "cancel", "resend"].includes(action)) {
                void refreshUser?.();
                showSnackbar({
                    text: i18n.t(`invitation.action_result.${action}.success`),
                    mode: "success",
                });
            }
            navigation.popToTop();
        },
        [navigation, refreshUser, showSnackbar, i18n],
    );

    const onActionError = useCallback(() => {
        showSnackbar({
            text: i18n.t(`invitation.action_result.accept.error`),
            mode: "error",
        });
    }, [showSnackbar, i18n]);

    const invitation = invitationCode
        ? invitationByCode?.__typename === "InvitationCodeSuccess"
            ? invitationByCode.invitation
            : undefined
        : data?.getInvitation;
    const target = invitation?.target;

    const { onAction, processingAction } = useInvitationHandler({
        onActionSuccess,
        onActionError,
        target,
    });

    const [initWorkerProfile, { loading: initWorkerProfileLoading }] = useMutation(CREATE_WORKER);

    const [rejectInvitationByCode, { loading: rejectInvitationByCodeLoading }] = useMutation(
        REJECT_INVITATION,
        // No account yet: nothing of the user's to refresh.
        { refetchQueries: [] },
    );

    const setSignupInvitationIdsToIgnore = useSetAtom(signupInvitationIdsToIgnore);

    const ignoredInvitationId = invitationId ?? invitation?.id;
    const onBeforeScreenRemove = useCallback(() => {
        if (ignoredInvitationId) {
            setSignupInvitationIdsToIgnore((r) => [...r, ignoredInvitationId]);
        }
    }, [ignoredInvitationId, setSignupInvitationIdsToIgnore]);

    useOnBeforeScreenRemove(onBeforeScreenRemove);

    const styles = useStyles();
    const hasAccount = !!profileType;
    const closeScreenThenNavigate = useCloseScreenThenNavigate();

    const functionWithAlert = useCallback(
        (fn: () => void | Promise<void>, withAlert = false) => {
            if (withAlert) {
                setAlert({
                    title: i18n.t(`screens.invitation.alerts.${target}.title`, {
                        clientName: invitation?.clientName || "",
                    }),
                    message: i18n.t(`screens.invitation.alerts.${target}.description`),
                    buttons: [
                        {
                            text: i18n.t(`screens.invitation.alerts.${target}.confirm`),
                            onPress: () => {
                                void fn();
                            },
                            isPreferred: true,
                        },
                        {
                            text: i18n.t("cancel"),
                            style: "cancel",
                        },
                    ],
                });
            } else {
                void fn();
            }
        },
        [i18n, invitation?.clientName, setAlert, target],
    );

    const accept = useCallback(() => {
        if (invitation) {
            switch (target) {
                case InvitationTarget.Client:
                    if (hasAccount) {
                        functionWithAlert(
                            () =>
                                // Opened from inside the app, this screen can be a modal owned by a
                                // nested navigator (todo list) while CheckClientInfo lives on the
                                // root navigator: close it first, otherwise the pushed screen stays
                                // behind the still-presented modal on iOS.
                                closeScreenThenNavigate(() => {
                                    navigation.navigate("CheckClientInfo", {
                                        isNewProfile: true,
                                        invitation,
                                    });
                                }),
                            true,
                        );
                    } else {
                        // Signup flow: both screens belong to the same navigator, nothing to close.
                        navigation.navigate("CheckClientInfo", {
                            isNewProfile: false,
                            invitation,
                            invitationCode,
                        });
                    }
                    break;
                case InvitationTarget.Worker: {
                    if (!hasAccount) {
                        navigation.replace("CheckWorkerInfo", {
                            invitation,
                            invitationCode,
                        });
                    } else {
                        if (!user?.workerProfile) {
                            functionWithAlert(async () => {
                                try {
                                    const { data: initWorkerProfileData } = await initWorkerProfile(
                                        {
                                            variables: {
                                                workerProfile: {
                                                    invitationToken: invitation.token,
                                                },
                                            },
                                        },
                                    );

                                    closeScreenThenNavigate(() => {
                                        navigation.navigate("PostOnboardingProfileEditor", {
                                            newProfileId: `worker:${initWorkerProfileData?.createWorkerProfile.id}`,
                                        });
                                    });
                                } catch {
                                    showSnackbar({
                                        text: i18n.t(`invitation.action_result.accept.error`),
                                        mode: "error",
                                    });
                                }
                            }, true);
                        } else {
                            void onAction("accept", invitation);
                        }
                    }
                }
            }
        }
    }, [
        closeScreenThenNavigate,
        functionWithAlert,
        hasAccount,
        i18n,
        initWorkerProfile,
        invitation,
        invitationCode,
        navigation,
        onAction,
        showSnackbar,
        target,
        user?.workerProfile,
    ]);

    const reject = useCallback(async () => {
        if (!invitation) {
            return;
        }
        if (!invitationCode) {
            void onAction("reject", invitation);
            return;
        }
        // Declined before any account exists: the code proves the invitation is the caller's.
        let rejected: boolean;
        try {
            const { data: rejectData } = await rejectInvitationByCode({
                variables: { token: invitation.token, code: invitationCode },
            });
            rejected = !!rejectData?.rejectInvitation;
        } catch {
            rejected = false;
        }
        if (rejected) {
            showSnackbar({
                text: i18n.t("invitation.action_result.reject.success"),
                mode: "success",
            });
            // Back to the role selection, like an invitation sent to the phone number.
            navigation.popToTop();
        } else {
            showSnackbar({
                text: i18n.t("invitation.action_result.reject.error"),
                mode: "error",
            });
        }
    }, [
        i18n,
        invitation,
        invitationCode,
        navigation,
        onAction,
        rejectInvitationByCode,
        showSnackbar,
    ]);

    useEffect(() => {
        // check if invitation still exists,
        // backend return null if invitation does not exist or is not accessible by the current user
        if (getInvitationError) {
            void apolloClient.refetchQueries({
                include: [GET_CLIENT_WORKERS_AND_INVITATION_WORKERS],
            });
            // notify user
            showSnackbar({
                text: i18n.t(`invitation.invitation_not_found`),
                mode: "warning",
            });
            if (navigation.canGoBack()) {
                navigation.goBack();
            } else {
                navigation.popToTop();
            }
        }
    }, [
        getInvitationError,
        getInvitationLoading,
        i18n,
        invitation,
        invitationId,
        navigation,
        showSnackbar,
    ]);

    useEffect(() => {
        // The code was checked just before, but the invitation may have been answered or cancelled
        // since: say why, and leave.
        const codeError =
            invitationByCode?.__typename === "InvitationCodeError"
                ? errorMessageForCode(invitationByCode.messageKey)
                : invitationByCodeError
                  ? errorMessageForCode()
                  : undefined;
        if (codeError) {
            showSnackbar({ text: codeError, mode: "warning" });
            if (navigation.canGoBack()) {
                navigation.goBack();
            } else {
                navigation.popToTop();
            }
        }
    }, [errorMessageForCode, invitationByCode, invitationByCodeError, navigation, showSnackbar]);

    const disabled =
        !!processingAction || initWorkerProfileLoading || rejectInvitationByCodeLoading;
    const processing = disabled;

    const onLinkPress = useOpenMarkdownLink();

    return (
        <ScreenCard isContentContainerFlex>
            <VStack gap={1} h="2/3" v={1}>
                <InvitationImage width="100%" style={{ overflow: "visible" }} />
                <View style={styles.container}>
                    <UserAvatar avatarPath={invitation?.clientLogoUrl} size={48} />
                    <Gap gap="2/3" />
                    <MarkdownView
                        markdown={i18n.t(`screens.invitation.descriptions.${target}`, {
                            clientName: escapeMarkdown(invitation?.clientName),
                            senderName: escapeMarkdown(invitation?.senderName),
                        })}
                        style={styles.description}
                        onLinkPress={onLinkPress}
                    />
                </View>
            </VStack>
            <View style={{ flex: 1 }} />
            <HStack h="2/3" v={1}>
                <Button
                    size="large"
                    buttonMode="soft"
                    onPress={() => void reject()}
                    label={i18n.t(`screens.invitation.reject`)}
                    style={styles.rejectButton}
                    disabled={disabled}
                />
                <Gap gap={1} />
                <Button
                    size="large"
                    buttonMode="secondary"
                    onPress={() => void accept()}
                    label={i18n.t("screens.invitation.accept")}
                    style={styles.acceptButton}
                    processing={processing}
                    disabled={disabled}
                />
            </HStack>
        </ScreenCard>
    );
};

const useStyles = () => {
    const { gridSize } = useTheme();
    return StyleSheet.create({
        container: {
            flexDirection: "row",
        },
        description: {
            flex: 1,
            marginTop: -gridSize / 3,
        },
        buttonsContainer: {
            flexDirection: "row",
        },
        rejectButton: {
            flex: 3,
            borderWidth: 0,
        },
        acceptButton: {
            flex: 5,
        },
    });
};

export default Invitation;
